/**
 * V70G6B — 엔글 오목 Lobby.
 *   방 만들기 / 방 코드 입력 선택 화면.
 *   / tournament / game / omok  라우트에 매핑.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Hash, Plus, ArrowRight } from 'lucide-react';
import { useOmokActions, saveLastRoomId, resolveResumableRoomId } from './useOmokRoom';
import { activeGameRoomIds } from './omokView';
import { OmokActiveGameConfirm } from './OmokActiveGameConfirm';

/** V70G8E — 진행 중 대국 때문에 보류된 입장 요청 */
type PendingEntry = { kind: 'create' } | { kind: 'join'; code: string };

export function OmokLobbyPage() {
  const navigate = useNavigate();
  const { createRoom, joinRoom, busy, error, clearError } = useOmokActions();
  const [roomCode, setRoomCode] = useState<string>('');
  const [lastRoomInfo, setLastRoomInfo] = useState<string | null>(null);
  // V70G8E — 서버가 진행 중 대국을 알려 주면 기권 확인 팝업 (확인 전에는 입장하지 않음)
  const [pending, setPending] = useState<{ entry: PendingEntry; activeRoomIds: string[] } | null>(null);

  useEffect(() => {
    // V70G8J — 승패가 확정된 방은 복귀 안내 대상이 아님 (방 상태 확인 후 참조 정리)
    let cancelled = false;
    void resolveResumableRoomId().then((last) => {
      if (!cancelled) setLastRoomInfo(last);
    });
    return () => { cancelled = true; };
  }, []);

  const enter = async (entry: PendingEntry, resignRoomIds?: string[]) => {
    try {
      const r = entry.kind === 'create'
        ? await createRoom(resignRoomIds)
        : await joinRoom(entry.code, resignRoomIds);
      setPending(null);
      saveLastRoomId(r.roomId);
      navigate(`/tournament/game/omok/room/${r.roomId}`);
    } catch (e) {
      // 진행 중 대국 → 확인 팝업 (재요청 중 새 대국이 생겼으면 목록 갱신). 그 외 오류는 hook 이 표시.
      const ids = activeGameRoomIds(e);
      if (ids) setPending({ entry, activeRoomIds: ids });
    }
  };

  const handleCreate = () => enter({ kind: 'create' });
  const handleJoin = () => {
    const code = roomCode.trim();
    if (!/^\d{6}$/.test(code)) return;
    void enter({ kind: 'join', code });
  };
  const handleConfirmResign = () => {
    if (!pending) return;
    clearError();
    void enter(pending.entry, pending.activeRoomIds);
  };
  const handleCancelResign = () => {
    // [취소] → 진행 중 대국 유지, 새 방 진입 안 함. 진행 중 대국 화면으로 복귀.
    const back = pending?.activeRoomIds[0];
    setPending(null);
    clearError();
    if (back) navigate(`/tournament/game/omok/room/${back}`);
  };
  const handleResumeLast = () => {
    if (lastRoomInfo) navigate(`/tournament/game/omok/room/${lastRoomInfo}`);
  };

  return (
    <div style={{ padding: 24, background: '#f8fafc', minHeight: '100vh' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>
          엔글 <span style={{ color: '#065f46' }}>오목</span>
        </h1>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>
          2인 실시간 대전. 방을 만들어 코드를 상대방에게 알려주거나, 받은 코드로 참가하세요.
        </p>

        {pending && (
          <OmokActiveGameConfirm
            busy={busy}
            error={error}
            targetLabel={pending.entry.kind === 'create' ? '새 방 만들기' : `${pending.entry.code} 방 참가`}
            onCancel={handleCancelResign}
            onConfirm={handleConfirmResign}
          />
        )}

        {error && !pending && (
          <div style={{
            padding: 12, background: '#fee2e2', border: '1px solid #fecaca',
            borderRadius: 8, color: '#991b1b', marginBottom: 16, fontSize: 13,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span>{error}</span>
            <button onClick={clearError} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#991b1b', fontSize: 18 }}>×</button>
          </div>
        )}

        {lastRoomInfo && (
          <div style={{
            padding: 10, background: '#fef3c7', border: '1px solid #fcd34d',
            borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#92400e',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span>진행 중이던 방으로 복귀 가능합니다.</span>
            <button
              style={{ padding: '4px 10px', background: '#f59e0b', color: '#fff', border: 0, borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
              onClick={handleResumeLast}
            >복귀 →</button>
          </div>
        )}

        {/* 방 만들기 */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Plus size={20} color="#065f46" />
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>방 만들기</h2>
          </div>
          <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>
            새 방을 열면 6자리 코드가 발급됩니다. 상대방에게 코드를 알려주세요.
          </p>
          <button
            style={primaryBtnStyle(!busy)}
            onClick={handleCreate}
            disabled={busy}
            data-testid="omok-create-room"
          >
            {busy ? '방 생성 중…' : <>방 만들기 <ArrowRight size={16} /></>}
          </button>
        </div>

        {/* 방 참가 */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Hash size={20} color="#065f46" />
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>방 코드 입력</h2>
          </div>
          <input
            type="text"
            placeholder="6자리 숫자 코드"
            value={roomCode}
            onChange={(e) => setRoomCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            disabled={busy}
            style={{
              width: '100%', boxSizing: 'border-box', padding: 12, fontSize: 20,
              border: '2px solid #cbd5e1', borderRadius: 8, textAlign: 'center',
              letterSpacing: 8, fontFamily: 'monospace', marginBottom: 10,
            }}
            data-testid="omok-room-code-input"
          />
          <button
            style={primaryBtnStyle(!busy && /^\d{6}$/.test(roomCode))}
            onClick={handleJoin}
            disabled={busy || !/^\d{6}$/.test(roomCode)}
            data-testid="omok-join-room"
          >
            {busy ? '참가 중…' : <>참가하기 <Users size={16} /></>}
          </button>
        </div>

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <button
            style={{ padding: '8px 16px', background: '#f3f4f6', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
            onClick={() => navigate('/tournament/games')}
          >← 게임센터로</button>
        </div>
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: '#fff', padding: 20, borderRadius: 10, border: '1px solid #e2e8f0',
  marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const primaryBtnStyle = (enabled: boolean): React.CSSProperties => ({
  width: '100%', padding: '12px 16px',
  background: enabled ? '#065f46' : '#94a3b8',
  color: '#fff', border: 0, borderRadius: 8,
  cursor: enabled ? 'pointer' : 'not-allowed',
  fontSize: 15, fontWeight: 700,
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
});

export default OmokLobbyPage;
